import { classNames, transformDateToLocalDateString, transformRoute } from "helpers";
import { FleetVehicleRow } from "services/api/fleet/vehicles/types";
import { VehicleListingRow } from "services/api/sales/listings/types";
import { CHANNEL_CONFIG, CHANNEL_SORT_ORDER, DEFAULT_CHANNEL_CONFIG } from "services/api/sales/listings/consts";
import { CopyButton } from "ui/components/Button/CopyButton";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import Tooltip from "ui/components/Tooltip/Tooltip";
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";
import { getListingStatus } from "ui/modules/sales/pages/listings/consts";
import { ReactSVG } from "react-svg";
import { findMajorityPrice } from "./priceOutliers";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const FUEL_TYPE_VARIANT: Record<string, BadgeVariant> = {
  B: "warning",
  P: "warning",
  D: "info",
  E: "success",
  H: "success-dark",
  LPG: "danger",
  CNG: "danger",
};

const TRANSMISSION_VARIANT: Record<string, BadgeVariant> = {
  A: "info",
  M: "warning",
  S: "success",
};

const pickVariant = (map: Record<string, BadgeVariant>, code: string | null | undefined, fallback: BadgeVariant): BadgeVariant =>
  (code && map[code.toUpperCase()]) || fallback;

export const LISTING_VEHICLE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  listings: VehicleListingRow[],
  onNewListing?: (row: FleetVehicleRow) => void
): Column[] => [
  {
    title: "",
    key: "image",
    field: "image",
    alwaysVisible: true,
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const listing = listings.find((l) => l.fleetVehicleId === row.id);
      const hasActiveChannel = listing?.channels?.some((ch) => ch.isActive) ?? false;
      const barColor = getListingStatus(!!listing, hasActiveChannel).barClassName;
      const fullName = [
        row.vehicleManufacturer?.name,
        row.vehicleModel?.name,
        row.vehicleType?.name,
      ].filter(Boolean).join(" ");
      const trigger = (
        <div className="cursor-help">
          <TableIcon
            imageUrl={row.picture}
            initials={row.vehicleManufacturer?.name?.toString().charAt(0).toUpperCase()}
            imageFit="cover"
          />
        </div>
      );
      return (
        <div
          className={classNames(
            "after:absolute after:-left-0.5 after:-top-0.5 after:bottom-0 after:w-[3px] after:content-['']",
            barColor
          )}
        >
          <Tooltip position="top-right" size="medium" trigger={trigger}>
            <div className="min-w-[280px]">
              {row.picture && (
                <div className="mb-3 rounded overflow-hidden">
                  <img src={row.picture} alt={fullName} className="w-full h-[120px] object-cover rounded" />
                </div>
              )}
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Status oglasa</div>
              <div className="flex justify-between text-sm mb-3 pb-2 border-b border-slate-600">
                <span className="text-slate-300">Stanje</span>
                {(() => {
                  const status = getListingStatus(!!listing, hasActiveChannel);

                  return (
                    <span className={classNames("inline-flex items-center gap-1.5 font-medium", status.textClassName)}>
                      <span className={classNames("w-1.5 h-1.5 rounded-full", status.dotClassName)} />
                      {status.label}
                    </span>
                  );
                })()}
              </div>
              <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-300">Naziv</span>
                <span className="font-medium text-right max-w-[180px] truncate">{fullName || "-"}</span>
              </div>
              {row.vinNumber && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">VIN</span>
                  <span className="font-medium text-xs">{row.vinNumber}</span>
                </div>
              )}
              {row.registrationPlate && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Registracija</span>
                  <span className="font-medium">{row.registrationPlate}</span>
                </div>
              )}
              {row.firstRegistrationDate != null && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Prva registracija</span>
                  <span className="font-medium">{transformDateToLocalDateString(row.firstRegistrationDate)}</span>
                </div>
              )}
              {row.colorName && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Boja</span>
                  <span className="font-medium">{row.colorName}</span>
                </div>
              )}
              {(row.vehicleType?.maximumPowerKw != null || row.vehicleType?.maximumPowerHp != null) && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Snaga</span>
                  <span className="font-medium">{row.vehicleType?.maximumPowerKw ?? "-"} kW / {row.vehicleType?.maximumPowerHp ?? "-"} KS</span>
                </div>
              )}
              {row.vehicleType?.cylinderCapacity && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Zapremina</span>
                  <span className="font-medium">{row.vehicleType.cylinderCapacity} cm³</span>
                </div>
              )}
              {row.vehicleType?.bodyCode && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Karoserija</span>
                  <span className="font-medium">{row.vehicleType.bodyCode}</span>
                </div>
              )}
              {row.vehicleType?.numberOfDoors && row.vehicleType?.seats && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Konfiguracija</span>
                  <span className="font-medium">{row.vehicleType.numberOfDoors} vrata / {row.vehicleType.seats} sjedala</span>
                </div>
              )}
              {row.vehicleType?.manufacturerCode && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Šifra proizvođača</span>
                  <span className="font-medium">{row.vehicleType.manufacturerCode}</span>
                </div>
              )}
              {row.kmTraveled != null && row.kmTraveled > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">Kilometraža</span>
                  <span className="font-medium">{row.kmTraveled.toLocaleString("hr-HR")} km</span>
                </div>
              )}
            </div>
          </Tooltip>
        </div>
      );
    },
  },
  {
    // Stanje oglasa dosad se vidjelo samo kao crta uz sliku i u tooltipu — u tablici mora stajati
    // kao podatak, jer se po njemu i filtrira rad.
    title: "admin.sales.listing.search.status",
    key: "listingStatus",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const listing = listings.find((l) => l.fleetVehicleId === row.id);
      const hasActiveChannel = listing?.channels?.some((ch) => ch.isActive) ?? false;

      const status = getListingStatus(!!listing, hasActiveChannel);

      return <Badge text={status.label} variant={status.variant} height="small" showDot />;
    },
  },
  {
    title: "admin.fleet.vehicle.search.manufacturer",
    key: "manufacturer",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      return (
        <div className="flex items-center gap-2">
          {row.vehicleManufacturer && (
            <img src={row.vehicleManufacturer?.image ?? ""} alt="" className="w-12 flex-shrink-0 rounded-full object-cover" />
          )}
          <div>{row.vehicleManufacturer?.name}</div>
        </div>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.search.model",
    key: "model",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const yearRange = row.vehicleModel?.yearFrom && row.vehicleModel?.yearTo
        ? `${row.vehicleModel.yearFrom} – ${row.vehicleModel.yearTo}`
        : row.vehicleModel?.yearFrom
          ? `${row.vehicleModel.yearFrom} –`
          : row.vehicleModel?.year
            ? `${row.vehicleModel.year}`
            : null;
      const manufacturerCode = row.vehicleModel?.manufacturerCode;
      return (
        <div>
          <div className="text-md mb-1">
            {row.vehicleModel?.name}
            {manufacturerCode ? ` (${manufacturerCode})` : ""}
          </div>
          {yearRange && (
            <div className="text-xs text-slate-500">
              {t("admin.fleet.vehicle.search.vehiclemodel.year")}: {yearRange}
            </div>
          )}
        </div>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.search.type",
    key: "type",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const showEngine = row.vehicleType?.engine && row.vehicleType.engine !== row.vehicleType.name;
      const parts = [
        row.vehicleType?.maximumPowerKw != null || row.vehicleType?.maximumPowerHp != null
          ? `${row.vehicleType?.maximumPowerKw}kW / ${row.vehicleType?.maximumPowerHp} KS`
          : null,
        row.vehicleType?.drivenWheels?.name ?? null,
      ].filter(Boolean);
      return (
        <div>
          <div className="text-md mb-0.5">
            {row.vehicleType?.name}
            {showEngine ? ` ${row.vehicleType?.engine}` : ""}
          </div>
          {parts.length > 0 && (
            <div className="text-xs text-slate-500">{parts.join(" · ")}</div>
          )}
        </div>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.search.fueltype",
    key: "fuelType",
    field: "",
    type: ColumnTypes.custom,
    defaultVisible: false,
    render: (row: FleetVehicleRow) =>
      row.vehicleType?.fuelType ? (
        <Badge
          text={row.vehicleType.fuelType.name ?? ""}
          variant={pickVariant(FUEL_TYPE_VARIANT, row.vehicleType.fuelType.id, "warning")}
          height="small"
          showDot
        />
      ) : null,
  },
  {
    title: "admin.fleet.vehicle.search.transmission",
    key: "transmission",
    field: "",
    type: ColumnTypes.custom,
    defaultVisible: false,
    render: (row: FleetVehicleRow) =>
      row.vehicleType?.transmission ? (
        <Badge
          text={row.vehicleType.transmission.name ?? ""}
          variant={pickVariant(TRANSMISSION_VARIANT, row.vehicleType.transmission.id, "info")}
          height="small"
          showDot
        />
      ) : null,
  },
  {
    title: "admin.fleet.vehicle.search.registrationplate",
    key: "registrationPlate",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => (
      <div>
        {row.registrationPlate && (
          <div className="font-medium">
            {row.registrationPlate}
            <CopyButton value={row.registrationPlate} />
          </div>
        )}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.search.vinnumber",
    key: "vinNumber",
    field: "",
    type: ColumnTypes.custom,
     defaultVisible: false,
    render: (row: FleetVehicleRow) => (
      <div>
        {row.vinNumber && (
          <div className="font-medium">
            {row.vinNumber}
            <CopyButton value={row.vinNumber} />
          </div>
        )}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.search.currentkm",
    key: "currentKm",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => (
      <div className="text-md">
        {row.kmTraveled
          ? `${row.kmTraveled.toLocaleString("hr-HR")} km`
          : "-"}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.search.firstregistration",
    key: "firstRegistration",
    field: "",
    type: ColumnTypes.custom,
     defaultVisible: false,
    render: (row: FleetVehicleRow) => (
      <div className="text-md">
        {row.firstRegistrationDate
          ? `${transformDateToLocalDateString(row.firstRegistrationDate)}`
          : "-"}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.search.prices",
    key: "prices",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const listing = listings.find((l) => l.fleetVehicleId === row.id);
      const activeChannels = [...(listing?.channels?.filter((ch) => ch.isActive && ch.effectivePrice != null) ?? [])]
        .sort((a, b) => (CHANNEL_SORT_ORDER[a.salesChannelId] ?? 99) - (CHANNEL_SORT_ORDER[b.salesChannelId] ?? 99));

      // Ako nema listinga ili kanala, prikazi samo prodajnu cijenu
      if (activeChannels.length === 0) {
        return (
          <div className="text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500 font-bold">{t("admin.fleet.vehicle.search.sellingprice")}:</span>
              <span className="font-bold text-green-600">
                {row.price?.sellingPrice ? `${row.price.sellingPrice.toLocaleString("hr-HR")} €` : "-"}
              </span>
            </div>
          </div>
        );
      }

      // Provjeri jesu li sve cijene iste
      const prices = activeChannels.map((ch) => ch.effectivePrice);
      const allSame = prices.every((p) => p === prices[0]);

      if (allSame) {
        return (
          <div className="text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500 font-bold">{t("admin.fleet.vehicle.search.sellingprice")}:</span>
              <span className="font-bold text-green-600">
                {prices[0] ? `${prices[0].toLocaleString("hr-HR")} €` : "-"}
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{t("admin.sales.listing.search.sameprice")}</div>
          </div>
        );
      }

      // Cijena koja odstupa od većine ide žuto, da se na prvi pogled vidi koji oglasnik
      // strši. Bez jasne većine (sve različite ili izjednačene) ne boji se ništa.
      const majorityPrice = findMajorityPrice(prices);

      return (
        <div className="text-sm space-y-0.5">
          {activeChannels.map((ch) => {
            const cfg = CHANNEL_CONFIG[ch.salesChannelId] || DEFAULT_CHANNEL_CONFIG;
            const isOutlier =
              majorityPrice != null && ch.effectivePrice !== majorityPrice;

            return (
              <div key={ch.salesChannelId} className="flex items-center justify-between gap-3">
                <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${cfg.badge.bg} ${cfg.badge.text}`}>
                  <ReactSVG src={cfg.icon} className="w-3 h-3 [&_svg]:w-3 [&_svg]:h-3" />
                  {ch.name}
                </span>
                <span
                  className={classNames(
                    "whitespace-nowrap",
                    isOutlier ? "text-amber-600 font-bold" : "font-medium"
                  )}
                >
                  {ch.effectivePrice ? `${ch.effectivePrice.toLocaleString("hr-HR")} €` : "-"}
                </span>
              </div>
            );
          })}
        </div>
      );
    },
  },
  {
    title: "admin.sales.listing.search.channels",
    key: "channels",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const listing = listings.find((l) => l.fleetVehicleId === row.id);
      if (!listing || !listing.channels || listing.channels.length === 0) {
        return (
          <span className="text-xs text-gray-400">-</span>
        );
      }

      const activeChannels = [...listing.channels]
        .filter((ch) => ch.isActive)
        .sort((a, b) => (CHANNEL_SORT_ORDER[a.salesChannelId] ?? 99) - (CHANNEL_SORT_ORDER[b.salesChannelId] ?? 99));

      return (
        <div className="flex items-center -space-x-2">
          {activeChannels.map((ch) => {
            const cfg = CHANNEL_CONFIG[ch.salesChannelId] || DEFAULT_CHANNEL_CONFIG;
            return (
              <Tooltip
                key={ch.salesChannelId}
                position="top-center"
                size="small"
                trigger={
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${cfg.badge.bg} ${cfg.badge.text}`}
                  >
                    <ReactSVG src={cfg.icon} className="w-4 h-4 [&_svg]:w-4 [&_svg]:h-4" />
                  </span>
                }
              >
                <div className="min-w-[180px]">
                  <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">{ch.name}</div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">Status</span>
                    <span className="font-medium text-green-400">Aktivno</span>
                  </div>
                  {ch.effectivePrice != null && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-300">Cijena</span>
                      <span className="font-medium">€ {ch.effectivePrice.toLocaleString("hr-HR")}</span>
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      );
    },
  },
  {
    title: "",
    key: "actions",
    field: "",
    alwaysVisible: true,
    type: ColumnTypes.custom,
    render: (row: FleetVehicleRow) => {
      const listing = listings.find((l) => l.fleetVehicleId === row.id);
      const hasListing = !!listing;
      return (
        <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
          <ButtonLink
            startIcon="/media/icons/vehicle.svg"
            cssClassName="text-slate-600 hover:bg-blue-100"
            size="full"
            variant="text"
            to={transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id: row.id })}
          >
            Detalji vozila
          </ButtonLink>
          <ButtonMenuItem
            startIcon="/media/icons/plus.svg"
            size="full"
            disabled={hasListing}
            tooltip={hasListing ? "Vozilo već ima oglas" : undefined}
            onClick={() => onNewListing?.(row)}
          >
            Unesi novi oglas
          </ButtonMenuItem>
        </ButtonMenu>
      );
    },
  },
];
